import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { EXTENSIONS } from 'probonio-shared-ui/registry';
import { ExtensionInterface } from 'probonio-shared-ui/registry/extension-definition';
import { ReactNode } from 'react';
import { BenefitMetadataExtensionInterface } from './benefit-metadata.extension.interface';

export interface PayrollPreconditionGateProps {
  children: ReactNode;
}

/**
 * Allows a benefit to gate its payroll report behind a precondition (e.g. an external
 * onboarding has to be completed first). The gate wraps the payroll report content and
 * either renders `children` when the precondition is met, or a blocking screen otherwise.
 */
export abstract class BenefitPayrollPreconditionExtensionInterface extends ExtensionInterface {
  abstract PreconditionGate: React.ComponentType<PayrollPreconditionGateProps>;
}

const benefitPayrollPreconditionCache = new Map<BenefitDTOBenefitEnum, BenefitPayrollPreconditionExtensionInterface>();

/**
 * Retrieves the payroll precondition extension for a specific benefit type.
 * Uses caching to avoid redundant EXTENSIONS lookups.
 *
 * @param benefit - The benefit enum value (e.g., BenefitDTOBenefitEnum.BikeleasingV2
 * @returns The matched payroll precondition extension, or undefined if none exist
 */
export function getBenefitPayrollPrecondition(benefit: BenefitDTOBenefitEnum): BenefitPayrollPreconditionExtensionInterface | undefined {
  if (benefitPayrollPreconditionCache.has(benefit)) {
    return benefitPayrollPreconditionCache.get(benefit);
  }

  const extensionKeyForBenefit = EXTENSIONS.getKeyedProviders(BenefitMetadataExtensionInterface).find(
    ({ provider }) => provider.benefitId === benefit,
  )?.key;
  const payrollPreconditionProvider = EXTENSIONS.getProviderByKey(extensionKeyForBenefit, BenefitPayrollPreconditionExtensionInterface);

  if (!payrollPreconditionProvider) {
    return undefined;
  }

  benefitPayrollPreconditionCache.set(benefit, payrollPreconditionProvider);

  return payrollPreconditionProvider;
}
